import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Loader2, Search, FileText, AlertTriangle, RefreshCw,
  ListChecks, Users, Mail, Lock, Send, Repeat2, Archive, MoreHorizontal,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDay } from './AssignMentorDialog';
import {
  PlacementDialog, SendDialog, WithdrawDialog, FinalizeDialog, ReplacementsDialog,
  CloseAllocationDialog, FinalListsDialog,
} from './InternshipApplicationDialogs';

const COMPANY_PILL = {
  not_sent: 'bg-gray-100 text-gray-500', pending: 'bg-amber-50 text-amber-700',
  accepted: 'bg-emerald-50 text-emerald-600', rejected: 'bg-red-50 text-red-500',
  withdrawn: 'bg-gray-100 text-gray-400',
};
const COMPANY_LABEL = {
  not_sent: 'Not sent', pending: 'Awaiting response', accepted: 'Accepted',
  rejected: 'Rejected', withdrawn: 'Withdrawn',
};
const STAGE_LABEL = {
  not_shortlisted: 'Unallocated', suggested: 'Suggested', manually_assigned: 'Manually assigned',
  sent_to_company: 'Sent to company', finalized: 'Finalized', not_selected: 'Not selected',
};
const STAGE_PILL = {
  not_shortlisted: 'bg-gray-100 text-gray-500', suggested: 'bg-[#FEF0EC] text-[#F97D6A]',
  manually_assigned: 'bg-violet-50 text-violet-600', sent_to_company: 'bg-sky-50 text-sky-600',
  finalized: 'bg-emerald-500 text-white', not_selected: 'bg-gray-100 text-gray-500',
};
const API_ROOT = process.env.REACT_APP_BACKEND_URL;

const APP_FOCUS = { finalizations_pending: 'ready_to_finalize', company_responses: 'awaiting_company' };

export default function InternshipApplicationsTab({ programId: lockedProgram, focus, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [programId, setProgramId] = useState(lockedProgram || null);
  const [view, setView] = useState('student');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState(APP_FOCUS[focus] || 'all');
  const [placing, setPlacing] = useState(null);
  const [selected, setSelected] = useState([]);
  const [sending, setSending] = useState(false);
  const [withdrawing, setWithdrawing] = useState(null);
  const [finalizing, setFinalizing] = useState(null);
  const [replacing, setReplacing] = useState(false);
  const [closing, setClosing] = useState(false);
  const [sendingFinal, setSendingFinal] = useState(false);
  const [busy, setBusy] = useState(false);
  const loadedOnce = useRef(false);

  const load = useCallback(async (pid) => {
    setLoading(true);
    try {
      const r = await api.get('/coordinator/internship-applications', { params: pid ? { program_id: pid } : {} });
      setData(r.data);
      setSelected([]);
      if (r.data?.board?.program?.id) setProgramId(r.data.board.program.id);
      if (loadedOnce.current) onChanged?.();
      loadedOnce.current = true;
    } catch { setData(null); }
    setLoading(false);
  }, [onChanged]);
  useEffect(() => { load(lockedProgram); }, [load, lockedProgram]);

  const board = data?.board;
  const apps = useMemo(() => board?.applications || [], [board]);
  const historyOnly = !!board?.program?.read_only && !!board?.summary?.finalized;

  useEffect(() => {
    if (historyOnly) setStatus(st => (st === 'all' ? 'finalized' : st));
  }, [historyOnly, board?.program?.id]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return apps.filter(a => {
      if (status !== 'all') {
        if (status === 'conflict') { if (!a.conflict) return false; }
        else if (status === 'awaiting_company') { if (a.company_status !== 'pending') return false; }
        else if (status === 'ready_to_finalize') { if (!a.can_finalize) return false; }
        else if (status === 'rejected_by_company') { if (a.company_status !== 'rejected') return false; }
        else if (status === 'finalized') { if (a.final_status !== 'selected') return false; }
        else if (status === 'unallocated') {
          if (a.final_status === 'selected' || a.allocation_status === 'suggested') return false;
        } else if (status === 'not_selected') { if (a.final_status !== 'not_selected') return false; }
      }
      if (!q) return true;
      return [a.student_name, a.grade, a.priority1?.title, a.priority1?.company_name,
        a.priority2?.title, a.priority2?.company_name].filter(Boolean)
        .some(v => String(v).toLowerCase().includes(q));
    });
  }, [apps, query, status]);

  const sendable = apps.filter(a => a.allocation_status === 'suggested'
    && a.company_status === 'not_sent' && !a.conflict && a.final_status !== 'selected');
  const finalizable = apps.filter(a => a.can_finalize);
  const actionable = [...new Set([...sendable, ...finalizable])];
  const selectedSendable = sendable.filter(a => selected.includes(a.id));
  const selectedFinalizable = finalizable.filter(a => selected.includes(a.id));
  const toggleOne = (id) => setSelected(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));

  const recalc = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/coordinator/internship-programs/${programId}/recalculate`);
      setData(d => ({ ...d, board: r.data }));
      toast.success('Suggestions refreshed');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not refresh suggestions'); }
    setBusy(false);
  };

  const retryEmails = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/coordinator/internship-programs/${programId}/retry-notifications`);
      setData(d => ({ ...d, board: r.data }));
      toast.success(`${r.data.emails.sent} sent · ${r.data.emails.failed} still failing`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not resend the emails'); }
    setBusy(false);
  };

  if (loading) return <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[#9CA3AF]" /></div>;
  if (!board) {
    return <p className="text-center py-12 text-[#9CA3AF] text-sm max-w-md mx-auto leading-relaxed" data-testid="applications-empty">
      Create an internship programme first. Applications from parents will appear here once the programme is open.
    </p>;
  }

  const s = board.summary;
  const closed = !!board.allocation_closed;
  const readOnly = !!board.program.read_only;
  const locked = closed || readOnly;
  const schoolMode = board.placement_mode === 'school_finalizes';
  const applicationsOpen = !!board.program.applications_open;
  const unallocated = apps.filter(a => a.final_status !== 'selected' && a.allocation_status !== 'suggested').length;

  const metrics = readOnly
    ? [['Total applicants', s.total_applications], ['Placements finalized', s.finalized],
      ['Not selected', s.not_selected]]
    : applicationsOpen
      ? [['Total applicants', s.total_applications], ['Provisional matches', s.suggested],
        ['Unallocated students', unallocated], ['Available seats', s.seats_remaining]]
      : [['Total applicants', s.total_applications], ['Ready to finalize', s.ready_to_finalize],
        ['Finalized', s.finalized], ['Available seats', s.seats_remaining]];

  const message = readOnly
    ? `This programme is closed. Applications and placements are read-only — the records below are kept for reference.${board.closure.closed_at ? ` Allocation closed on ${formatDay(board.closure.closed_at.slice(0, 10))}.` : ''}`
    : closed
      ? `Allocation closed by ${board.closure.closed_by} on ${formatDay(board.closure.closed_at?.slice(0, 10))}.`
      : applicationsOpen
        ? `Parents can still edit their preferences until ${formatDay(board.program.application_deadline)}. Matches below are provisional — placements can be finalised once the deadline passes.`
        : schoolMode
          ? 'Applications are closed. Adjust the suggested allocations if you need to, then finalise the placements and send each company its final list.'
          : 'Applications are closed. Finalise the students companies have accepted; others can still be replaced.';

  const moreActions = [
    !locked && { key: 'refresh', label: 'Refresh suggestions', icon: RefreshCw, run: recalc },
    !locked && !schoolMode && selectedSendable.length > 0 && {
      key: 'send-to-companies', label: `Send ${selectedSendable.length} to companies`,
      icon: Send, run: () => setSending(true),
    },
    !locked && !schoolMode && { key: 'review-replacements', label: 'Review replacements', icon: Repeat2, run: () => setReplacing(true) },
    !readOnly && schoolMode && board.can_send_final_lists && {
      key: 'send-final-lists', label: 'Send final lists', icon: Send, run: () => setSendingFinal(true),
    },
    !locked && board.closure.can_close && { key: 'close-allocation', label: 'Close allocation', icon: Archive, run: () => setClosing(true) },
    !readOnly && s.notifications_pending > 0 && {
      key: 'retry-emails', label: `Retry ${s.notifications_pending} parent email${s.notifications_pending === 1 ? '' : 's'}`,
      icon: Mail, run: retryEmails,
    },
  ].filter(Boolean);

  const filters = [['all', `All (${apps.length})`],
    ...(!schoolMode && s.company_awaiting ? [['awaiting_company', `Awaiting company (${s.company_awaiting})`]] : []),
    ...(!locked && s.ready_to_finalize ? [['ready_to_finalize', `Ready to finalize (${s.ready_to_finalize})`]] : []),
    ...(!schoolMode && s.company_rejected ? [['rejected_by_company', `Rejected, needs review (${s.company_rejected})`]] : []),
    ...(s.finalized ? [['finalized', `Finalized (${s.finalized})`]] : []),
    ...(unallocated ? [['unallocated', `Unallocated (${unallocated})`]] : []),
    ...(s.not_selected ? [['not_selected', `Not selected (${s.not_selected})`]] : []),
    ...(s.conflicts.length ? [['conflict', `Conflicts (${s.conflicts.length})`]] : []),
  ];

  return (
    <div data-testid="applications-tab">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {!lockedProgram && (
          <Select value={programId || ''} onValueChange={v => load(v)}>
            <SelectTrigger title={board?.program?.name} className="h-9 w-[270px] rounded-lg border-gray-200 text-xs bg-white" data-testid="applications-program-select">
              <SelectValue placeholder="Programme" />
            </SelectTrigger>
            <SelectContent>
              {(data.programs || []).map(p => (
                <SelectItem key={p.id} value={p.id} className="text-xs">{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div className="flex gap-1 bg-[#F3F4F6] rounded-lg p-1">
          {[['student', 'By Student'], ['internship', 'By Internship']].map(([id, label]) => (
            <button key={id} onClick={() => setView(id)} className={`px-3 py-1 text-[11px] font-semibold rounded-md transition-colors ${view === id ? 'bg-white text-[#1F2937] shadow-sm' : 'text-[#6B7280]'}`} data-testid={`applications-view-${id}`}>{label}</button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[160px]">
          <Search className="h-3.5 w-3.5 text-[#9CA3AF] absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search student or internship" className="h-9 pl-8 rounded-lg border-gray-200 text-xs bg-white" data-testid="applications-search" />
        </div>
        {!locked && finalizable.length > 0 && (
          <Button size="sm" onClick={() => setFinalizing(selectedFinalizable.length ? selectedFinalizable : finalizable)} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-lg text-xs" data-testid="finalize-btn">
            <ListChecks className="h-3.5 w-3.5 mr-1" />
            Finalize {(selectedFinalizable.length || finalizable.length)} placement{(selectedFinalizable.length || finalizable.length) === 1 ? '' : 's'}
          </Button>
        )}
        {moreActions.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" disabled={busy} className="rounded-lg text-xs border-gray-200" data-testid="more-actions-btn">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <MoreHorizontal className="h-3.5 w-3.5 mr-1" />}More actions
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-xl">
              {moreActions.map(a => (
                <DropdownMenuItem key={a.key} onSelect={a.run} className="text-xs cursor-pointer" data-testid={`action-${a.key}`}>
                  <a.icon className="h-3.5 w-3.5 mr-2 text-[#9CA3AF]" />{a.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <div className="bg-white rounded-xl border border-[#E5E7EB] px-4 py-3 mb-3" data-testid="allocation-summary">
        <div className="flex flex-wrap gap-x-8 gap-y-2">
          {metrics.map(([label, value]) => (
            <div key={label} data-testid={`metric-${label.toLowerCase().replace(/ /g, '-')}`}>
              <p className="text-lg font-semibold text-[#1F2937] leading-none font-['Outfit']">{value}</p>
              <p className="text-[11px] text-[#6B7280] mt-1">{label}</p>
            </div>
          ))}
        </div>
        <p className={`text-[11px] mt-2.5 flex items-start gap-1 ${readOnly || closed ? 'text-emerald-700' : 'text-[#6B7280]'}`} data-testid="allocation-message">
          {(readOnly || closed) && <Lock className="h-3 w-3 mt-px shrink-0" />}{message}
        </p>
      </div>

      {!readOnly && s.conflicts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-3" data-testid="conflicts-banner">
          <p className="text-xs font-semibold text-amber-800 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" />{s.conflicts.length} placement{s.conflicts.length === 1 ? '' : 's'} need your review</p>
          <ul className="mt-1 space-y-0.5">
            {s.conflicts.map(c => <li key={c.application_id} className="text-[11px] text-amber-700">{c.student_name} — {c.detail}</li>)}
          </ul>
        </div>
      )}

      {filters.length > 1 && (
        <div className="flex gap-1.5 mb-3 flex-wrap">
          {filters.map(([f, label]) => (
            <button key={f} onClick={() => setStatus(f)} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${status === f ? 'bg-[#1F2937] text-white border-[#1F2937]' : 'bg-white text-[#6B7280] border-gray-200 hover:border-[#9CA3AF]'}`} data-testid={`applications-filter-${f}`}>{label}</button>
          ))}
        </div>
      )}

      {view === 'student'
        ? (
          <StudentView
            rows={filtered} closed={locked} schoolMode={schoolMode} onPlace={setPlacing}
            selected={selected} toggleOne={toggleOne} actionable={actionable}
            onSelectAll={() => setSelected(selected.length === actionable.length ? [] : actionable.map(a => a.id))}
            onWithdraw={setWithdrawing} onFinalizeOne={a => setFinalizing([a])}
          />
        )
        : <InternshipView groups={board.by_internship} query={query} schoolMode={schoolMode} />}

      {placing && (
        <PlacementDialog
          app={placing} onClose={() => setPlacing(null)}
          onDone={b => { setPlacing(null); setData(d => ({ ...d, board: b })); }}
        />
      )}
      {sending && (
        <SendDialog
          rows={selectedSendable} programId={programId}
          onClose={() => setSending(false)}
          onDone={() => { setSending(false); load(programId); }}
        />
      )}
      {withdrawing && (
        <WithdrawDialog
          row={withdrawing} onClose={() => setWithdrawing(null)}
          onDone={() => { setWithdrawing(null); load(programId); }}
        />
      )}
      {finalizing && (
        <FinalizeDialog
          board={board} rows={finalizing} onClose={() => setFinalizing(null)}
          onDone={b => { setFinalizing(null); setSelected([]); setData(d => ({ ...d, board: b })); }}
        />
      )}
      {replacing && (
        <ReplacementsDialog
          programId={programId} onClose={() => setReplacing(false)}
          onSent={() => { setReplacing(false); load(programId); }}
        />
      )}
      {closing && (
        <CloseAllocationDialog
          board={board} onClose={() => setClosing(false)}
          onDone={b => { setClosing(false); setData(d => ({ ...d, board: b })); }}
        />
      )}
      {sendingFinal && (
        <FinalListsDialog
          board={board} onClose={() => setSendingFinal(false)}
          onDone={b => { setSendingFinal(false); setData(d => ({ ...d, board: b })); }}
        />
      )}
    </div>
  );
}

// ───────────────────────────── By Student ─────────────────────────────
function StudentView({ rows, closed, schoolMode, onPlace, selected, toggleOne, actionable, onSelectAll, onWithdraw, onFinalizeOne }) {
  if (!rows.length) return <p className="text-center py-10 text-[#9CA3AF] text-sm" data-testid="student-view-empty">No applications match these filters.</p>;
  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-x-auto" data-testid="applications-by-student">
      <table className="w-full text-left min-w-[980px]">
        <thead>
          <tr className="text-[10px] uppercase tracking-wide text-[#9CA3AF] border-b border-[#F3F4F6]">
            {!closed && (
              <th className="pl-4 py-2.5 font-semibold w-8">
                <input type="checkbox" checked={actionable.length > 0 && selected.length === actionable.length}
                  onChange={onSelectAll} className="accent-[#F97D6A]" data-testid="select-all-sendable" />
              </th>
            )}
            <th className="px-4 py-2.5 font-semibold">Student</th>
            <th className="px-3 py-2.5 font-semibold">Preferences</th>
            <th className="px-3 py-2.5 font-semibold">Applied</th>
            <th className="px-3 py-2.5 font-semibold">Allocation stage</th>
            {!schoolMode && <th className="px-3 py-2.5 font-semibold">Company decision</th>}
            <th className="px-3 py-2.5 font-semibold">Final placement</th>
            <th className="px-3 py-2.5 font-semibold text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(a => (
            <tr key={a.id} className="border-b border-[#F7F8F9] last:border-0 hover:bg-[#FAFBFC]" data-testid={`application-row-${a.id}`}>
              {!closed && (
                <td className="pl-4 py-3 align-top">
                  {actionable.some(x => x.id === a.id) ? (
                    <input type="checkbox" checked={selected.includes(a.id)} onChange={() => toggleOne(a.id)}
                      className="accent-[#F97D6A]" data-testid={`select-${a.id}`} />
                  ) : <span className="text-[#E5E7EB]">—</span>}
                </td>
              )}
              <td className="px-4 py-3 align-top">
                <p className="text-sm font-semibold text-[#1F2937]">{a.student_name}</p>
                <p className="text-[11px] text-[#9CA3AF]">Grade {a.grade}{a.revision_count > 0 ? ` · edited ${a.revision_count}×` : ''}</p>
                {a.resume && (
                  <a href={`${API_ROOT}/api/parent/internship-applications/${a.id}/resume`} target="_blank" rel="noreferrer" className="text-[10px] text-[#F97D6A] hover:underline inline-flex items-center gap-1 mt-1" data-testid={`resume-link-${a.id}`}>
                    <FileText className="h-3 w-3" />Resume
                  </a>
                )}
              </td>
              <td className="px-3 py-3 align-top">
                <Pref rank={1} p={a.priority1} />
                <Pref rank={2} p={a.priority2} />
              </td>
              <td className="px-3 py-3 align-top text-[11px] text-[#4B5563] whitespace-nowrap">{formatDay(a.first_submitted_at?.slice(0, 10))}</td>
              <td className="px-3 py-3 align-top">
                <Badge className={`border-0 text-[10px] ${STAGE_PILL[a.allocation_stage]}`} data-testid={`allocation-stage-${a.id}`}>
                  {STAGE_LABEL[a.allocation_stage]}
                </Badge>
                {a.suggested && a.allocation_stage !== 'not_selected' && (
                  <>
                    <p className="text-[11px] font-medium text-[#1F2937] mt-1">{a.suggested.title}</p>
                    <p className="text-[10px] text-[#6B7280]">{a.suggested.company_name}
                      {a.suggested_source === 'manual' ? ' · coordinator choice'
                        : a.suggested_source === 'priority1' ? ' · Priority 1' : ' · Priority 2'}</p>
                  </>
                )}
                {a.conflict && <p className="text-[10px] text-amber-700 mt-0.5 flex items-start gap-1"><AlertTriangle className="h-3 w-3 shrink-0 mt-px" />{a.conflict}</p>}
                {a.override_reason && <p className="text-[10px] text-[#9CA3AF] mt-0.5">“{a.override_reason}” — {a.override_by}</p>}
              </td>
              {!schoolMode && (
                <td className="px-3 py-3 align-top">
                  <Badge className={`border-0 text-[10px] ${COMPANY_PILL[a.company_status]}`} data-testid={`company-status-${a.id}`}>
                    {COMPANY_LABEL[a.company_status] || a.company_status}
                  </Badge>
                  {a.proposal?.sent_at && (
                    <p className="text-[10px] text-[#9CA3AF] mt-1">Sent {formatDay(a.proposal.sent_at.slice(0, 10))} by {a.proposal.sent_by}</p>
                  )}
                  {a.proposal?.decided_at && (
                    <p className="text-[10px] text-[#6B7280] mt-0.5">Replied {formatDay(a.proposal.decided_at.slice(0, 10))} · {a.proposal.decided_by}</p>
                  )}
                  {a.proposal?.reason && <p className="text-[10px] text-[#6B7280] mt-0.5">“{a.proposal.reason}”</p>}
                  {!closed && a.company_status === 'pending' && a.final_status !== 'selected' && (
                    <button onClick={() => onWithdraw(a)} className="text-[10px] text-[#F97D6A] hover:underline mt-1" data-testid={`withdraw-${a.id}`}>
                      Withdraw proposal
                    </button>
                  )}
                </td>
              )}
              <td className="px-3 py-3 align-top">
                {a.final_status === 'selected' ? (
                  <>
                    <Badge className="border-0 text-[10px] bg-emerald-500 text-white" data-testid={`final-status-${a.id}`}>Finalized</Badge>
                    <p className="text-[11px] font-medium text-[#1F2937] mt-1">{a.final?.title}</p>
                    <p className="text-[10px] text-[#6B7280]">{a.final?.company_name}</p>
                    <p className="text-[10px] text-[#9CA3AF] mt-0.5">By {a.finalized_by} · {formatDay(a.finalized_at?.slice(0, 10))}</p>
                    <span className={`text-[10px] ${a.notification_status === 'sent' ? 'text-[#9CA3AF]' : 'text-red-500'}`}>
                      {a.notification_status === 'sent' ? 'Parent notified' : a.notification_status === 'failed' ? 'Email failed' : 'Email pending'}
                    </span>
                  </>
                ) : a.final_status === 'not_selected' ? (
                  <Badge className="border-0 text-[10px] bg-gray-100 text-gray-500" data-testid={`final-status-${a.id}`}>Not selected</Badge>
                ) : (
                  <span className="text-[11px] text-[#9CA3AF]" data-testid={`final-status-${a.id}`}>Not finalized</span>
                )}
              </td>
              <td className="px-3 py-3 align-top text-right">
                {a.can_finalize ? (
                  <div className="flex flex-col items-end gap-1">
                    <Button size="sm" onClick={() => onFinalizeOne(a)} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-lg text-[11px] h-7" data-testid={`finalize-one-${a.id}`}>Finalize</Button>
                    {!closed && (
                      <button onClick={() => onPlace(a)} className="text-[10px] text-[#F97D6A] hover:underline" data-testid={`adjust-placement-${a.id}`}>Adjust</button>
                    )}
                  </div>
                ) : a.final_status === 'selected' ? (
                  <span className="text-[10px] text-emerald-600 inline-flex items-center gap-1"><Lock className="h-3 w-3" />Locked</span>
                ) : (
                  <div className="flex flex-col items-end gap-1">
                    {!closed && <Button size="sm" variant="outline" onClick={() => onPlace(a)} className="rounded-lg text-[11px] h-7 border-gray-200" data-testid={`adjust-placement-${a.id}`}>Adjust</Button>}
                    {a.finalize_blocker && <span className="text-[10px] text-[#9CA3AF] max-w-[170px] leading-snug" data-testid={`finalize-blocker-${a.id}`}>{a.finalize_blocker}</span>}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const Pref = ({ rank, p }) => (p ? (
  <div className="flex items-start gap-1.5">
    <span className="text-[10px] text-[#9CA3AF] mt-0.5">P{rank}</span>
    <div>
      <p className="text-[11px] font-medium text-[#1F2937]">{p.title}</p>
      <p className="text-[10px] text-[#6B7280]">{p.company_name}</p>
    </div>
  </div>
) : null);

// ─────────────────────────── By Internship ───────────────────────────
function InternshipView({ groups, query, schoolMode }) {
  const q = query.trim().toLowerCase();
  const shown = q ? groups.filter(g => [g.title, g.company_name].some(v => String(v).toLowerCase().includes(q))
    || g.applicants.some(a => a.student_name.toLowerCase().includes(q))) : groups;
  if (!shown.length) return <p className="text-center py-10 text-[#9CA3AF] text-sm" data-testid="internship-view-empty">No approved internships match this search.</p>;
  return (
    <div className="space-y-2" data-testid="applications-by-internship">
      {shown.map(g => (
        <div key={g.id} className="bg-white rounded-xl border border-[#E5E7EB] px-4 py-3" data-testid={`internship-group-${g.id}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p className="text-sm font-semibold text-[#1F2937]">{g.title}</p>
              <p className="text-[11px] text-[#6B7280]">{g.company_name} · {formatDay(g.start_date)} – {formatDay(g.end_date)}</p>
            </div>
            <div className="text-right">
              <p className="text-[11px] text-[#4B5563] flex items-center gap-1"><Users className="h-3 w-3 text-[#9CA3AF]" />{g.finalized_count} of {g.positions} seats finalized</p>
              <p className="text-[10px] text-[#9CA3AF]">
                {schoolMode
                  ? `${g.filled} allocated · ${g.seats_available} seat${g.seats_available === 1 ? '' : 's'} available`
                  : `${g.accepted_count} accepted · ${g.pending_count} awaiting · ${g.seats_available} seat${g.seats_available === 1 ? '' : 's'} available`}
              </p>
            </div>
          </div>
          {g.applicants.length === 0 ? (
            <p className="text-[11px] text-[#9CA3AF] mt-2">No applicants yet.</p>
          ) : (
            <ul className="mt-2 divide-y divide-[#F7F8F9]">
              {g.applicants.map(a => (
                <li key={a.id} className="py-1.5 flex items-center justify-between gap-3" data-testid={`group-applicant-${g.id}-${a.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge className={`border-0 text-[10px] ${a.preference === 1 ? 'bg-[#FEF0EC] text-[#F97D6A]' : 'bg-[#F3F4F6] text-[#6B7280]'}`}>P{a.preference}</Badge>
                    <span className="text-sm text-[#1F2937] truncate">{a.student_name}</span>
                    <span className="text-[10px] text-[#9CA3AF]">Grade {a.grade}</span>
                  </div>
                  <span className="flex items-center gap-2">
                    {!schoolMode && (
                      <Badge className={`border-0 text-[10px] ${COMPANY_PILL[a.company_status]}`}>{COMPANY_LABEL[a.company_status]}</Badge>
                    )}
                    <span className={`text-[10px] font-semibold ${a.final_status === 'selected' && a.placed_here ? 'text-emerald-600' : a.placed_here ? 'text-[#4B5563]' : 'text-[#9CA3AF]'}`}>
                      {a.final_status === 'selected' && a.placed_here ? 'Finalized here'
                        : a.placed_here ? 'Shortlisted here'
                        : a.allocation_status === 'suggested' ? 'Placed elsewhere' : 'Unallocated'}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

